import { useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

import { MAIN_SLOT } from '@/components/TabBar';
import { useCovered, useInSheet } from '@/covered';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { useNativeMainButton } from '@/platform';
import { useTheme } from '@/theme';

const BTN_MAIN =
  'flex w-full items-center justify-between gap-3 px-4.5 py-4.75 text-left text-t15 font-extrabold active:transform-none';

function nextFrame(onChange: () => void): () => void {
  const id = requestAnimationFrame(onChange);
  return () => cancelAnimationFrame(id);
}

const findSlot = () => document.getElementById(MAIN_SLOT);

function useSlot(): HTMLElement | null {
  return useSyncExternalStore(nextFrame, findSlot, () => null);
}

export function MainAction({
  text,
  onClick,
  visible = true,
  waitingText,
  progress = false,
  icon = '→',
}: {
  text: string;
  onClick: () => void;
  visible?: boolean;
  waitingText?: string;
  progress?: boolean;
  icon?: string;
}) {
  const light = useTheme().theme === 'light';
  const covered = useCovered();
  const inSheet = useInSheet();
  const hasNative = useNativeMainButton({
    text,
    visible: visible && !covered,
    waitingText: covered ? undefined : waitingText,
    progress,
    light,
    onClick,
  });
  const slot = useSlot();

  if (hasNative) return null;

  if (covered || (!visible && !waitingText)) return null;

  const bar = !visible ? (
    <div
      data-main-action
      aria-disabled
      aria-busy={progress || undefined}
      className={cn(BTN_MAIN, 'bg-muted text-wait-fg cursor-default')}
    >
      <span className="min-w-0 text-pretty">{waitingText}</span>
      {progress && <Spinner aria-hidden className="size-4 flex-none" />}
    </div>
  ) : (
    <button
      type="button"
      data-main-action
      className={cn(BTN_MAIN, 'bg-primary text-primary-foreground')}
      onClick={onClick}
    >
      <span className="min-w-0 text-pretty">{text}</span>
      {progress ? (
        <Spinner aria-hidden className="size-4 flex-none" />
      ) : (
        <span className="text-t16 flex-none opacity-55" aria-hidden>
          {icon}
        </span>
      )}
    </button>
  );

  if (inSheet) return <div className="rounded-plate mt-1 overflow-hidden">{bar}</div>;
  return slot ? createPortal(bar, slot) : bar;
}
